import { baseApi } from './baseApi';
import type { JsonObject, JsonValue } from './baseQuery';

export interface LatamContract extends JsonObject {
    subscription_id: number;
    subscription_status: 'ACTIVE' | 'CANCELLATION_PENDING';
    asset_id: string;
    sku: string;
    asset_status: string;
    product_name: string;
    period_description: string;
}

const isJsonObject = (value: JsonValue): value is JsonObject =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const isLatamContract = (value: JsonValue): value is LatamContract =>
    isJsonObject(value) &&
    typeof value.subscription_id === 'number' &&
    (value.subscription_status === 'ACTIVE' || value.subscription_status === 'CANCELLATION_PENDING') &&
    typeof value.asset_id === 'string' &&
    typeof value.sku === 'string' &&
    typeof value.asset_status === 'string' &&
    typeof value.product_name === 'string' &&
    typeof value.period_description === 'string';

const mapLatamContracts = (value: JsonValue | null) => (Array.isArray(value) ? value.filter(isLatamContract) : []);

export const latamContractsApi = baseApi.injectEndpoints({
    endpoints: build => ({
        checkActiveContracts: build.query<LatamContract[], string>({
            query: assetId => ({
                service: 'latamMarketplace',
                path: '/v1/payment-gateway/subscription/assets-no-canceled',
                method: 'POST',
                body: { ids: [assetId] },
            }),
            transformResponse: mapLatamContracts,
            providesTags: (_result, _error, assetId) => [{ type: 'Contracts', id: assetId }],
        }),
        checkActiveContractsBulk: build.query<(LatamContract & { id: string })[], string[]>({
            query: assetIds => ({
                service: 'latamMarketplace',
                path: '/v1/payment-gateway/subscription/assets-no-canceled',
                method: 'POST',
                body: { ids: assetIds },
            }),
            transformResponse: rawResult =>
                mapLatamContracts(rawResult).map(contract => ({ ...contract, id: contract.asset_id })),
            providesTags: (_result, _error, assetIds) => assetIds.map(id => ({ type: 'Contracts' as const, id })),
        }),
    }),
});

export const { useCheckActiveContractsQuery, useCheckActiveContractsBulkQuery } = latamContractsApi;
